// src/components/common/CacheControlPanel.js
import React, { useState, useEffect, useCallback } from "react";
import {
  Paper,
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Button,
  Collapse,
  Tooltip,
  Alert,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import DeleteIcon from "@mui/icons-material/Delete";
import MemoryIcon from "@mui/icons-material/Memory";
import NuclearIcon from "@mui/icons-material/Whatshot";
import RefreshIcon from "@mui/icons-material/Refresh";
import apiClient from "../../services/EnhancedApiClient";
import { useEnvironment } from "../../context/EnvironmentContext";

/**
 * Cache Control Panel Component
 * Displays cache status and provides controls for managing the dashboard cache
 */
const CacheControlPanel = ({ onClearCache }) => {
  const [cacheInfo, setCacheInfo] = useState({
    entries: 0,
    size: 0,
    lastUpdated: null,
    endpoints: [],
  });
  const [expanded, setExpanded] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [showNuclearConfirm, setShowNuclearConfirm] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [nuclearInProgress, setNuclearInProgress] = useState(false);
  const [nuclearResult, setNuclearResult] = useState(null);
  const [fraudWarningsClearing, setFraudWarningsClearing] = useState(false);

  const { environment } = useEnvironment();

  // Calculate and update cache information
  const updateCacheInfo = useCallback(() => {
    try {
      // Get all localStorage keys related to dashboard
      const cacheEntries = [];
      let totalSize = 0;
      let latestTimestamp = 0;

      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);

        if (key && key.startsWith("dashboard_")) {
          try {
            const value = localStorage.getItem(key);
            // Special handling for dashboard_cache_version
            if (key === "dashboard_cache_version") {
              try {
                JSON.parse(value);
              } catch (parseErr) {
                console.warn("Invalid dashboard_cache_version format, fixing");
                // Fix by setting it to a valid JSON string
                localStorage.setItem(
                  "dashboard_cache_version",
                  JSON.stringify("1.0.0")
                );
                continue; // Skip this iteration
              }
            }
            const size = (value || "").length * 2; // Approximate size in bytes
            totalSize += size;

            // Parse the stored data
            const parsedData = JSON.parse(value);
            const timestamp = parsedData.timestamp || 0;

            if (timestamp > latestTimestamp) {
              latestTimestamp = timestamp;
            }

            // Extract endpoint name from key
            const endpoint = key.replace("dashboard_", "");

            cacheEntries.push({
              endpoint,
              size,
              timestamp,
              entries: getEntryCount(parsedData.data),
              formattedSize: formatSize(size),
            });
          } catch (err) {
            console.warn(`Error parsing cache for ${key}:`, err);
          }
        }
      }

      setCacheInfo({
        entries: cacheEntries.length,
        size: totalSize,
        lastUpdated: latestTimestamp ? new Date(latestTimestamp) : null,
        endpoints: cacheEntries.sort((a, b) => b.timestamp - a.timestamp),
      });
    } catch (error) {
      console.error("Error gathering cache info:", error);
    }
  }, []); // Empty dependencies array as this doesn't depend on any external values

  // Count the number of entries in a data object
  const getEntryCount = (data) => {
    if (!data) return 0;

    // For array data
    if (Array.isArray(data)) {
      return data.length;
    }

    // For objects with specific properties like alerts
    if (data.alerts && Array.isArray(data.alerts)) {
      return data.alerts.length;
    }

    if (data.declines && Array.isArray(data.declines)) {
      return data.declines.length;
    }

    // For fraud warnings
    if (data.warnings && Array.isArray(data.warnings)) {
      return data.warnings.length;
    }

    // For other objects, count properties
    return Object.keys(data).length;
  };

  // Format size to readable string
  const formatSize = (bytes) => {
    if (bytes < 1024) {
      return `${bytes} B`;
    } else if (bytes < 1024 * 1024) {
      return `${(bytes / 1024).toFixed(1)} KB`;
    } else {
      return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    }
  };

  // Clear single endpoint cache
  const clearEndpointCache = (endpoint) => {
    setRefreshing(true);
    apiClient
      .clearCacheForEndpoint(endpoint)
      .then(() => {
        console.log(`Cache cleared for endpoint: ${endpoint}`);
        updateCacheInfo();
      })
      .catch((error) => {
        console.error(`Error clearing cache for ${endpoint}:`, error);
      })
      .finally(() => {
        setRefreshing(false);
      });
  };

  // Clear all cache with confirmation
  const clearAllCache = () => {
    setRefreshing(true);
    apiClient
      .clearCache()
      .then(() => {
        updateCacheInfo();
        setShowConfirm(false);

        // Call parent callback if provided
        if (onClearCache) {
          onClearCache();
        }
      })
      .catch((error) => {
        console.error("Error clearing all cache:", error);
      })
      .finally(() => {
        setRefreshing(false);
      });
  };

  // Method 1: Clear fraud warnings cache specifically
  const clearFraudWarningsCache = async () => {
    setFraudWarningsClearing(true);
    try {
      console.log("🗑️ Clearing fraud warnings cache...");

      // Clear API client cache for fraud warnings endpoint
      apiClient.clearCacheForEndpoint("/api/admin/fraud-warnings");

      // Clear localStorage cache if it exists
      const fraudWarningsCacheKey = "dashboard_fraud-warnings";
      if (localStorage.getItem(fraudWarningsCacheKey)) {
        localStorage.removeItem(fraudWarningsCacheKey);
        console.log("✅ Removed fraud warnings from localStorage");
      }

      // Clear any related localStorage keys
      Object.keys(localStorage).forEach((key) => {
        if (key.includes("fraud-warnings") || key.includes("warnings")) {
          localStorage.removeItem(key);
          console.log(`Removed localStorage key: ${key}`);
        }
      });

      // Use apiClient.get with force=true instead of direct fetch to avoid CORS issues
      try {
        console.log("🔄 Forcing fresh fetch through apiClient...");
        const data = await apiClient.get("/api/admin/fraud-warnings", {
          force: true,
          skipCache: true,
        });

        console.log("✅ Fresh fraud warnings data:", data);

        // Trigger force refresh event for fraud warnings panel
        window.dispatchEvent(new CustomEvent("force-refresh-fraud-warnings"));

        // Update cache info display
        updateCacheInfo();

        alert(
          "Fraud warnings cache cleared successfully! Panel should refresh with fresh data."
        );
      } catch (fetchError) {
        console.log(
          "⚠️ API client fetch failed, trying alternative approach..."
        );

        // Alternative: Just clear cache and trigger refresh without fetching
        console.log("🔄 Clearing cache and triggering panel refresh...");

        // Trigger force refresh event for fraud warnings panel
        window.dispatchEvent(new CustomEvent("force-refresh-fraud-warnings"));

        // Update cache info display
        updateCacheInfo();

        alert(
          "Fraud warnings cache cleared! Panel will refresh automatically."
        );
      }
    } catch (error) {
      console.error("❌ Error clearing fraud warnings cache:", error);
      alert(`Error clearing fraud warnings cache: ${error.message}`);
    } finally {
      setFraudWarningsClearing(false);
    }
  };

  // Method 2: Disable mock data temporarily
  const disableMockDataTemporarily = () => {
    // Temporarily override the NODE_ENV check
    const originalEnv = process.env.NODE_ENV;
    Object.defineProperty(process.env, "NODE_ENV", {
      value: "production",
      writable: true,
    });

    console.log("🔄 Temporarily disabled mock data");

    // Restore after 5 seconds
    setTimeout(() => {
      Object.defineProperty(process.env, "NODE_ENV", {
        value: originalEnv,
        writable: true,
      });
      console.log("🔄 Restored original NODE_ENV");
    }, 5000);

    alert("Mock data temporarily disabled for 5 seconds");
  };

  // Update cache info on mount and when expanded
  useEffect(() => {
    if (expanded) {
      updateCacheInfo();
    }
  }, [expanded, updateCacheInfo]);

  // Enhanced nuclear option that clears ALL data including fraud warnings
  const handleNuclearOption = async () => {
    if (environment !== "staging") {
      alert(
        "Nuclear option is only available in staging environment for safety!"
      );
      return;
    }

    setNuclearInProgress(true);
    setNuclearResult(null);

    try {
      console.log("🚨 NUCLEAR OPTION: Starting complete data deletion...");

      // Step 1: Clear all KV storage via API
      console.log("Step 1: Clearing all KV storage...");
      const kvResponse = await apiClient.post("/api/admin/nuke-all-kvs", {});

      if (!kvResponse.success) {
        throw new Error(`KV deletion failed: ${kvResponse.message}`);
      }

      // Step 2: Clear all local browser cache
      console.log("Step 2: Clearing browser cache...");
      apiClient.clearCache();

      // Step 3: Clear localStorage
      console.log("Step 3: Clearing localStorage...");
      const localStorageKeys = [];
      for (let i = localStorage.length - 1; i >= 0; i--) {
        const key = localStorage.key(i);
        if (
          key &&
          (key.startsWith("dashboard_") ||
            key.startsWith("auth_") ||
            key.startsWith("admin_"))
        ) {
          localStorageKeys.push(key);
          localStorage.removeItem(key);
        }
      }

      // Step 4: Clear sessionStorage
      console.log("Step 4: Clearing sessionStorage...");
      const sessionStorageKeys = [];
      for (let i = sessionStorage.length - 1; i >= 0; i--) {
        const key = sessionStorage.key(i);
        if (
          key &&
          (key.startsWith("dashboard_") ||
            key.startsWith("auth_") ||
            key.startsWith("admin_"))
        ) {
          sessionStorageKeys.push(key);
          sessionStorage.removeItem(key);
        }
      }

      // Step 5: Clear any cached fraud warnings data
      console.log("Step 5: Clearing fraud warnings cache...");

      // Step 6: Enhanced fraud warnings cache clearing
      console.log("Step 6: Force clearing fraud warnings cache...");

      // Clear the specific cache endpoint
      apiClient.clearCacheForEndpoint("/api/admin/fraud-warnings");

      // Override the cache mechanism temporarily
      const originalGet = apiClient.get.bind(apiClient);
      apiClient.get = async (endpoint, options = {}) => {
        if (endpoint === "/api/admin/fraud-warnings") {
          // Force bypass cache for fraud warnings
          return originalGet(endpoint, {
            ...options,
            force: true,
            skipCache: true,
          });
        }
        return originalGet(endpoint, options);
      };

      // Restore normal behavior after 10 seconds
      setTimeout(() => {
        apiClient.get = originalGet;
        console.log("🔄 Restored normal API cache behavior");
      }, 10000);

      // Update cache info to reflect changes
      updateCacheInfo();

      const result = {
        success: true,
        message: "Nuclear option completed successfully!",
        details: {
          kvDeleted: kvResponse.totalDeleted || 0,
          localStorageCleared: localStorageKeys.length,
          sessionStorageCleared: sessionStorageKeys.length,
          environment: environment,
          timestamp: new Date().toISOString(),
        },
      };

      setNuclearResult(result);
      console.log("🎉 NUCLEAR OPTION: Complete!", result);

      // Call parent callback if provided
      if (onClearCache) {
        onClearCache();
      }

      // Close confirmation dialog
      setShowNuclearConfirm(false);

      // Show success message for a few seconds, then reload
      setTimeout(() => {
        alert(
          `Nuclear option completed! Deleted ${result.details.kvDeleted} KV entries, ${result.details.localStorageCleared} localStorage items, and ${result.details.sessionStorageCleared} sessionStorage items. Page will reload.`
        );
        window.location.reload();
      }, 1000);
    } catch (error) {
      console.error("💥 NUCLEAR OPTION FAILED:", error);

      const errorResult = {
        success: false,
        message: `Nuclear option failed: ${error.message}`,
        error: error.message,
        timestamp: new Date().toISOString(),
      };

      setNuclearResult(errorResult);
      alert(`Nuclear option failed: ${error.message}`);
    } finally {
      setNuclearInProgress(false);
    }
  };

  return (
    <Paper sx={{ p: 3, mb: 3, overflow: "hidden" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        onClick={() => setExpanded(!expanded)}
        sx={{ cursor: "pointer" }}
      >
        <Box display="flex" alignItems="center">
          <MemoryIcon sx={{ mr: 1, color: "primary.main" }} />
          <Typography variant="h6" component="h2">
            Cache Control
            {environment === "staging" && (
              <Chip
                label="STAGING"
                size="small"
                color="warning"
                sx={{ ml: 1 }}
              />
            )}
          </Typography>
        </Box>
        <Box display="flex" alignItems="center">
          <Typography variant="body2" color="text.secondary" sx={{ mr: 2 }}>
            {cacheInfo.entries > 0 ? (
              <>
                {cacheInfo.entries} entries ({formatSize(cacheInfo.size)})
                {cacheInfo.lastUpdated &&
                  ` • Last update: ${cacheInfo.lastUpdated.toLocaleString()}`}
              </>
            ) : (
              "No cached data"
            )}
          </Typography>
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>

          {/* Quick Clear Fraud Warnings Button */}
          <Button
            variant="outlined"
            color="secondary"
            size="small"
            startIcon={<RefreshIcon />}
            onClick={(e) => {
              e.stopPropagation(); // Prevent expanding/collapsing
              clearFraudWarningsCache();
            }}
            disabled={fraudWarningsClearing}
            sx={{ ml: 1 }}
          >
            {fraudWarningsClearing ? "Clearing..." : "Clear Warnings"}
          </Button>

          {/* Disable Mock Data Button - Only in development */}
          {process.env.NODE_ENV === "development" && (
            <Button
              variant="outlined"
              color="info"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                disableMockDataTemporarily();
              }}
              sx={{ ml: 1 }}
            >
              Disable Mock
            </Button>
          )}

          {/* Nuclear Option Button - Only show in staging */}
          {environment === "staging" && (
            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={<NuclearIcon />}
              onClick={(e) => {
                e.stopPropagation(); // Prevent expanding/collapsing
                setShowNuclearConfirm(true);
              }}
              disabled={nuclearInProgress}
              sx={{ ml: 1 }}
            >
              {nuclearInProgress ? "Nuking..." : "Nuclear Option"}
            </Button>
          )}
        </Box>
      </Box>

      <Collapse in={expanded} timeout="auto">
        <Box sx={{ mt: 2 }}>
          {/* Nuclear Result Alert */}
          {nuclearResult && (
            <Alert
              severity={nuclearResult.success ? "success" : "error"}
              sx={{ mb: 2 }}
              onClose={() => setNuclearResult(null)}
            >
              <Typography variant="subtitle2" gutterBottom>
                {nuclearResult.message}
              </Typography>
              {nuclearResult.details && (
                <Typography variant="caption" component="div">
                  KV Entries: {nuclearResult.details.kvDeleted} • localStorage:{" "}
                  {nuclearResult.details.localStorageCleared} • sessionStorage:{" "}
                  {nuclearResult.details.sessionStorageCleared}
                </Typography>
              )}
            </Alert>
          )}

          {cacheInfo.endpoints.length > 0 ? (
            <>
              <Box display="flex" justifyContent="flex-end" mb={2}>
                {showConfirm ? (
                  <Box
                    sx={{
                      p: 2,
                      bgcolor: "#fff3cd",
                      borderRadius: 1,
                      border: "1px solid #ffeeba",
                      width: "100%",
                      display: "flex",
                      flexDirection: "column",
                      gap: 2,
                    }}
                  >
                    <DialogContentText>
                      Clear all cached data? Dashboard will reload data from
                      server.
                    </DialogContentText>
                    <Box display="flex" gap={1} justifyContent="flex-end">
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={clearAllCache}
                        disabled={refreshing}
                      >
                        {refreshing ? "Clearing..." : "Yes, Clear All"}
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => setShowConfirm(false)}
                        disabled={refreshing}
                      >
                        Cancel
                      </Button>
                    </Box>
                  </Box>
                ) : (
                  <Button
                    variant="contained"
                    color="error"
                    size="small"
                    startIcon={<DeleteIcon />}
                    onClick={() => setShowConfirm(true)}
                    disabled={refreshing}
                  >
                    Clear All Cache
                  </Button>
                )}
              </Box>

              <TableContainer sx={{ maxHeight: 300 }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      <TableCell>Endpoint</TableCell>
                      <TableCell align="center">Entries</TableCell>
                      <TableCell align="right">Size</TableCell>
                      <TableCell>Last Updated</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {cacheInfo.endpoints.map((entry) => (
                      <TableRow key={entry.endpoint} hover>
                        <TableCell>
                          <Tooltip title={`API: ${entry.endpoint}`}>
                            <Typography
                              variant="body2"
                              noWrap
                              sx={{ maxWidth: 200 }}
                            >
                              {entry.endpoint}
                              {entry.endpoint.includes("fraud-warnings") && (
                                <Chip
                                  label="NEW"
                                  size="small"
                                  color="info"
                                  sx={{ ml: 1, fontSize: "0.6rem" }}
                                />
                              )}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell align="center">{entry.entries}</TableCell>
                        <TableCell align="right">
                          {entry.formattedSize}
                        </TableCell>
                        <TableCell>
                          {entry.timestamp
                            ? new Date(entry.timestamp).toLocaleString()
                            : "N/A"}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title="Clear cache">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => clearEndpointCache(entry.endpoint)}
                              disabled={refreshing}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Box
              sx={{
                p: 3,
                bgcolor: "#f8f9fa",
                borderRadius: 1,
                textAlign: "center",
              }}
            >
              <Typography color="text.secondary">
                No cached data available
              </Typography>
            </Box>
          )}
        </Box>
      </Collapse>

      {/* Nuclear Confirmation Dialog */}
      <Dialog
        open={showNuclearConfirm}
        onClose={() => !nuclearInProgress && setShowNuclearConfirm(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ color: "error.main" }}>
          <Box display="flex" alignItems="center">
            <NuclearIcon sx={{ mr: 1 }} />
            ⚠️ NUCLEAR OPTION - STAGING ONLY
          </Box>
        </DialogTitle>
        <DialogContent>
          <Alert severity="error" sx={{ mb: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              This will PERMANENTLY DELETE ALL DATA:
            </Typography>
          </Alert>

          <List>
            <ListItem>
              <ListItemText
                primary="🗄️ All KV Storage Data"
                secondary="Fraud alerts, card declines, fraud warnings, user contexts, payment tracking"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="🧠 All In-Memory Caches"
                secondary="API client cache, service caches, fraud database cache"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="💾 All Browser Storage"
                secondary="localStorage, sessionStorage, dashboard cache"
              />
            </ListItem>
            <ListItem>
              <ListItemText
                primary="⚠️ Enhanced: Fraud Warnings Data"
                secondary="All address mismatch warnings, review data, and enhanced cache clearing"
              />
            </ListItem>
          </List>

          <Divider sx={{ my: 2 }} />

          <Typography variant="body2" color="text.secondary" paragraph>
            Current Environment: <strong>{environment.toUpperCase()}</strong>
          </Typography>

          <Typography variant="body1" color="error.main" fontWeight="bold">
            This action cannot be undone! Use only for testing or when you need
            a completely clean slate.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setShowNuclearConfirm(false)}
            disabled={nuclearInProgress}
          >
            Cancel
          </Button>
          <Button
            onClick={handleNuclearOption}
            color="error"
            variant="contained"
            disabled={nuclearInProgress || environment !== "staging"}
            startIcon={
              nuclearInProgress ? (
                <CircularProgress size={20} />
              ) : (
                <NuclearIcon />
              )
            }
          >
            {nuclearInProgress ? "Deleting Everything..." : "DELETE EVERYTHING"}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};

export default CacheControlPanel;